import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Stack,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Speed as BacktestIcon,
  Psychology as AiIcon,
  Assessment as EvaluationIcon,
  Notifications as SignalIcon,
  AllInclusive as UnlimitedIcon,
} from '@mui/icons-material';
import DWLFLoader from '../../components/DWLFLoader';

const QUOTA_CONFIG = {
  backtest: {
    label: 'Backtests',
    icon: BacktestIcon,
    description: 'Strategy backtesting requests',
  },
  aiAgent: {
    label: 'AI Agent',
    icon: AiIcon,
    description: 'AI-powered analysis and chat',
  },
  evaluation: {
    label: 'Evaluations',
    icon: EvaluationIcon,
    description: 'Strategy evaluation runs',
  },
  signal: {
    label: 'Signals',
    icon: SignalIcon,
    description: 'Trade signal generation',
  },
};

const getProgressColor = (used, limit) => {
  if (limit === null || limit === 0) return 'primary';
  const percentage = (used / limit) * 100;
  if (percentage >= 90) return 'error';
  if (percentage >= 70) return 'warning';
  return 'primary';
};

const formatResetTime = (resetAt) => {
  if (!resetAt) return null;
  const reset = new Date(resetAt);
  const now = new Date();
  const diff = reset - now;

  if (diff <= 0) return 'Resets soon';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `Resets in ${days}d`;
  }
  if (hours > 0) {
    return `Resets in ${hours}h ${minutes}m`;
  }
  return `Resets in ${minutes}m`;
};

const QuotaBar = ({ label, used, limit, remaining, period }) => {
  const isUnlimited = limit === null;
  // Handle division by zero: if limit is 0, show 100% (fully used) when used > 0, otherwise 0%
  const percentage = isUnlimited ? 0 : (limit === 0 ? (used > 0 ? 100 : 0) : Math.min((used / limit) * 100, 100));
  const color = getProgressColor(used, limit);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {isUnlimited ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <UnlimitedIcon sx={{ fontSize: 14 }} />
              Unlimited
            </Box>
          ) : (
            `${used} / ${limit}`
          )}
        </Typography>
      </Box>
      {!isUnlimited && (
        <LinearProgress
          variant="determinate"
          value={percentage}
          color={color}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: 'var(--dwlf-ink-10)',
          }}
        />
      )}
    </Box>
  );
};

const QuotaStatusCard = ({ quotaType, quotaData }) => {
  const config = QUOTA_CONFIG[quotaType] || {
    label: quotaType,
    icon: BacktestIcon,
    description: '',
  };
  const Icon = config.icon;

  const { limits, usage, remaining, hasOverride } = quotaData || {};

  const dailyLimit = limits?.daily;
  const monthlyLimit = limits?.monthly;
  const dailyUsed = usage?.daily || 0;
  const monthlyUsed = usage?.monthly || 0;

  // Determine if quotas are exhausted
  const dailyExhausted = dailyLimit !== null && dailyUsed >= dailyLimit;
  const monthlyExhausted = monthlyLimit !== null && monthlyUsed >= monthlyLimit;
  const isExhausted = dailyExhausted || monthlyExhausted;

  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: 4,
        borderColor: isExhausted ? 'error.main' : 'primary.main',
        backgroundColor: 'background.paper',
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Icon sx={{ color: isExhausted ? 'error.main' : 'primary.main' }} />
          <Typography variant="h6" component="div">
            {config.label}
          </Typography>
          {hasOverride && (
            <Tooltip title="Custom limit set by admin">
              <Chip label="Custom" size="small" color="secondary" />
            </Tooltip>
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {config.description}
        </Typography>

        {dailyLimit != null || dailyUsed > 0 ? (
          <QuotaBar
            label="Daily"
            used={dailyUsed}
            limit={dailyLimit}
            remaining={remaining?.daily}
            period="daily"
          />
        ) : null}

        {monthlyLimit != null || monthlyUsed > 0 ? (
          <QuotaBar
            label="Monthly"
            used={monthlyUsed}
            limit={monthlyLimit}
            remaining={remaining?.monthly}
            period="monthly"
          />
        ) : null}

        {dailyLimit == null && monthlyLimit == null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
            <UnlimitedIcon />
            <Typography variant="body2">Unlimited usage</Typography>
          </Box>
        )}

        {isExhausted && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 1, fontStyle: 'italic' }}
          >
            {dailyExhausted ? 'Daily limit reached. Resets at midnight.' : 'Monthly limit reached.'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default QuotaStatusCard;
